export interface FigmaBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FigmaEffectPreview {
  type?: string;
  radius?: number;
  spread?: number;
  color?: string;
  offset?: { x: number; y: number };
}

export interface FigmaStylePreview {
  fill?: string;
  stroke?: string;
  strokeWeight?: number;
  radius?: number;
  opacity?: number;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
  textAlign?: string;
  textVerticalAlign?: string;
  lineHeight?: number;
  gradient?: string | { css?: string };
  gradients?: Array<{ css?: string }>;
  imageRef?: string;
  image?: { imageRef?: string; scaleMode?: string };
  scaleMode?: string;
  effects?: FigmaEffectPreview[];
  effectsCss?: string;
  rotation?: number;
  clipsContent?: boolean;
  blendMode?: string;
}

export interface FigmaPreviewNode {
  id: string;
  name: string;
  type: string;
  box: FigmaBox | null;
  style?: FigmaStylePreview;
  imageRef?: string;
  gradient?: string | { css?: string };
  gradients?: Array<{ css?: string }>;
  effects?: FigmaEffectPreview[];
  rotation?: number;
  clipsContent?: boolean;
  blendMode?: string;
}

export function figmaPreviewScale(frame: FigmaPreviewNode, maxWidth = 300, maxHeight = 520): number {
  const box = frame.box;
  if (!box) return 1;
  return Math.min(1, maxWidth / box.w, maxHeight / box.h);
}

export function figmaPreviewBoardStyle(frame: FigmaPreviewNode): string {
  const box = frame.box ?? { w: 300, h: 520 };
  const scale = figmaPreviewScale(frame);
  return `width:${Math.max(1, Math.round(box.w * scale))}px;height:${Math.max(1, Math.round(box.h * scale))}px;`;
}

export function figmaPreviewNodeStyle(frame: FigmaPreviewNode, root: FigmaPreviewNode): string {
  const box = frame.box;
  const rootBox = root.box;
  if (!box || !rootBox) return "";
  const scale = figmaPreviewScale(root);
  return figmaPreviewNodeStyleAtScale(frame, root, scale);
}

export function figmaPreviewNodeStyleAtScale(frame: FigmaPreviewNode, root: FigmaPreviewNode, scale: number): string {
  const box = frame.box;
  const rootBox = root.box;
  if (!box || !rootBox) return "";
  return [
    `left:${Math.round((box.x - rootBox.x) * scale)}px`,
    `top:${Math.round((box.y - rootBox.y) * scale)}px`,
    `width:${Math.max(2, Math.round(box.w * scale))}px`,
    `height:${Math.max(2, Math.round(box.h * scale))}px`,
  ].join(";");
}

export function figmaGradientCss(frame: FigmaPreviewNode): string {
  const gradient = frame.style?.gradient ?? frame.gradient;
  if (typeof gradient === "string") return gradient;
  if (gradient?.css) return gradient.css;
  return frame.style?.gradients?.[0]?.css ?? frame.gradients?.[0]?.css ?? "";
}

export function figmaEffectsCss(frame: FigmaPreviewNode, root: FigmaPreviewNode): string {
  if (frame.style?.effectsCss) return frame.style.effectsCss;
  const scale = figmaPreviewScale(root);
  return figmaEffectsCssAtScale(frame, scale);
}

export function figmaEffectsCssAtScale(frame: FigmaPreviewNode, scale: number): string {
  const effects = frame.style?.effects ?? frame.effects ?? [];
  return effects
    .filter((effect) => effect.type === "DROP_SHADOW" || effect.type === "INNER_SHADOW")
    .map((effect) => {
      const inset = effect.type === "INNER_SHADOW" ? "inset " : "";
      const x = Math.round(Number(effect.offset?.x ?? 0) * scale);
      const y = Math.round(Number(effect.offset?.y ?? 0) * scale);
      const blur = Math.round(Number(effect.radius ?? 0) * scale);
      const spread = Math.round(Number(effect.spread ?? 0) * scale);
      return `${inset}${x}px ${y}px ${blur}px ${spread}px ${effect.color ?? "rgba(0,0,0,0.25)"}`;
    })
    .join(", ");
}

export function figmaStyleCss(frame: FigmaPreviewNode, root: FigmaPreviewNode): string {
  const scale = figmaPreviewScale(root);
  return figmaStyleCssAtScale(frame, scale);
}

export function figmaStyleCssAtScale(frame: FigmaPreviewNode, scale: number): string {
  const style = frame.style ?? {};
  const css: string[] = [];
  if (style.fill) css.push(frame.type === "TEXT" ? `color:${style.fill}` : `background:${style.fill}`);
  const gradientCss = figmaGradientCss(frame);
  if (gradientCss && frame.type !== "TEXT") css.push(`background:${gradientCss}`);
  if ((style.imageRef || style.image?.imageRef || frame.imageRef) && frame.type !== "TEXT") css.push(`background:${style.fill || "#111923"}`);
  if (style.stroke && Number(style.strokeWeight ?? 0) > 0) {
    css.push(`border:${Math.max(1, Math.round(Number(style.strokeWeight) * scale))}px solid ${style.stroke}`);
  }
  if (Number(style.radius ?? 0) > 0) css.push(`border-radius:${Math.round(Number(style.radius) * scale)}px`);
  if (typeof style.opacity === "number") css.push(`opacity:${style.opacity}`);
  const effectsCss = figmaEffectsCssAtScale(frame, scale);
  if (effectsCss) css.push(`box-shadow:${effectsCss}`);
  const rotation = Number(style.rotation ?? frame.rotation ?? 0);
  if (rotation !== 0) {
    css.push(`transform:rotate(${rotation}deg)`);
    css.push("transform-origin:center");
  }
  if (style.clipsContent || frame.clipsContent) css.push("overflow:hidden");
  const blendMode = style.blendMode ?? frame.blendMode;
  if (blendMode && blendMode !== "PASS_THROUGH" && blendMode !== "NORMAL") {
    css.push(`mix-blend-mode:${String(blendMode).toLowerCase().replace("_", "-")}`);
  }
  if (frame.type === "TEXT") {
    css.push(`font-size:${Math.max(6, Math.round(Number(style.fontSize || 12) * scale))}px`);
    css.push(`font-weight:${style.fontWeight ?? 400}`);
    if (style.fontFamily) css.push(`font-family:${style.fontFamily}, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`);
    if (Number(style.lineHeight ?? 0) > 0) css.push(`line-height:${Math.max(7, Math.round(Number(style.lineHeight) * scale))}px`);
    const align = String(style.textAlign ?? "LEFT").toLowerCase();
    css.push(`text-align:${align === "center" ? "center" : align === "right" ? "right" : "left"}`);
    const vertical = String(style.textVerticalAlign ?? "TOP");
    css.push(`align-items:${vertical === "CENTER" ? "center" : vertical === "BOTTOM" ? "flex-end" : "flex-start"}`);
  }
  return css.join(";");
}

export function figmaRenderNodeStyle(frame: FigmaPreviewNode, root: FigmaPreviewNode): string {
  return `${figmaPreviewNodeStyle(frame, root)}${figmaStyleCss(frame, root)}`;
}

export function figmaRenderNodeStyleAtScale(frame: FigmaPreviewNode, root: FigmaPreviewNode, scale: number): string {
  return `${figmaPreviewNodeStyleAtScale(frame, root, scale)}${figmaStyleCssAtScale(frame, scale)}`;
}
